export const translations = {
  en: {
    // Page Metadata
    meta: {
      home: {
        title: 'Nezz Consulting | FP&A boutique, Montréal',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      services: {
        title: 'Services | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      pricing: {
        title: 'Packages | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      whoWeServe: {
        title: 'Who We Serve | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      about: {
        title: 'About | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      insights: {
        title: 'Insights | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
      contact: {
        title: 'Contact | Nezz Consulting',
        description: 'FP&A process, templates, automated reporting and team training for finance teams at growing and PE-backed companies. Montréal, across Canada.',
      },
    },
    
    // Navbar
    nav: {
      home: 'Home',
      services: 'Services',
      pricing: 'Pricing',
      whoWeServe: 'Who We Serve',
      about: 'About',
      insights: 'Insights',
      contact: 'Contact',
    },
    
    // Home Page
    home: {
      heroTitle: 'Finance infrastructure for PE-backed companies',
      heroHighlight: 'from post-close to exit-ready',
      heroSubtitle: 'We build the reporting infrastructure, investor-grade FP&A, and post-merger finance function that PE sponsors expect — delivered fast, by operators who\'ve done it before.',
      getStarted: 'Schedule a Consultation',
      exploreServices: 'See Our Services',
      trustBar: 'Trusted by PE-backed companies and portfolio companies across North America',
      smbs: 'Growth Companies',
      peCompanies: 'PE Portfolio Companies',
      strategicPartners: 'Strategic Partners',
      ourServices: 'Our Services',
      servicesSubtitle: 'PE-grade financial planning, reporting, and integration - delivered by operators who understand the playbook.',
      whoWeServeTitle: 'Who We Serve',
      whoWeServeSubtitle: 'Built for PE sponsors, portfolio companies, and operators navigating complex growth.',
      learnMore: 'Learn More',
      ctaTitle: 'Ready to upgrade your finance function?',
      ctaSubtitle: 'Let\'s talk about how we can help you build institutional-grade financial infrastructure.',
      scheduleConsultation: 'Schedule a Consultation',
      problem90DayTitle: 'The 90-Day Post-Close Problem',
      problem90DayText: 'When a company is acquired by private equity, the finance function must transform overnight. Investors expect monthly EBITDA reporting, adjusted add-back documentation, board packs, and working capital dashboards — often before the management team has ever produced them. We solve this in 90 days. We parachute in, assess the current finance infrastructure, and build everything your PE sponsor needs to see — on time, every month.',
      problem90DayCTA: 'See how it works',
      peBackedCard: {
        title: 'PE-Backed Portfolio Companies',
        body: 'PE ownership changes everything about how your finance function needs to operate. We build the reporting infrastructure, EBITDA framework, and board-ready packages your sponsor expects — starting from Day 1 post-close.',
      },
      smbCard: {
        title: 'High-Growth SMBs',
        body: 'You\'re scaling fast and your financial processes haven\'t kept up. We bring enterprise-grade FP&A discipline, driver-based models, and executive dashboards — built for the pace of a growing company.',
      },
    },
    
    // Stats
    stats: {
      budgetManaged: '$1B+ Budget and Forecast Managed',
      yearsExperience: '15+ Years of PE & FP&A Experience',
      projectsDelivered: '50+ Projects Delivered',
      clientSatisfaction: '100% Client Satisfaction',
    },
    
    // Post-Merger Integration Page
    pmi: {
      heroTitle: 'Post-Merger Integration — Finance Workstream',
      heroSubtitle: 'We build the financial infrastructure that makes your bolt-on work.',
      getStarted: 'Tell us about your deal',
      problemTitle: 'The Problem',
      problemDescription: 'Most portfolio companies that close a bolt-on acquisition are not ready for what comes next. Two companies. Two charts of accounts. Two close calendars. Two reporting formats. One PE sponsor expecting consolidated financials within 30 days. The finance team is overwhelmed before the ink is dry. We\'ve been here before. We know exactly what needs to happen and in what order.',
      solutionTitle: 'What We Deliver',
      phase1Title: 'Days 1–30: Foundation',
      phase1Items: [
        'Reporting gap assessment',
        'Chart of accounts mapping and harmonization',
        'Close calendar alignment',
        'Day 1 reporting package',
      ],
      phase2Title: 'Days 31–60: Build',
      phase2Items: [
        'Combined 3-statement financial model',
        'Adjusted EBITDA framework and add-back registry',
        'Management and sponsor reporting templates',
        'Working capital baseline',
      ],
      phase3Title: 'Days 61–100: Stabilize',
      phase3Items: [
        'First full consolidated monthly close',
        'Board pack delivery',
        'KPI dashboard live',
        'Finance team handoff and documentation',
      ],
      ctaTitle: 'Ready to integrate fast?',
      ctaSubtitle: 'Let\'s build the financial infrastructure your bolt-on needs.',
      scheduleCall: 'Tell us about your deal',
    },

    
    // Services Page
    services: {
      title: 'Services',
      subtitle: 'PE-grade FP&A, reporting, and financial infrastructure - built for speed and institutional standards.',
      notSure: 'Not sure which service you need?',
      notSureText: 'Let\'s talk about where you are and where you need to be.',
      
      fpa: {
        title: 'PE-Grade FP&A & Investor Reporting',
        short: 'Institutional-quality financial planning and reporting that PE sponsors expect.',
        full: 'PE ownership demands a completely different level of financial reporting. We design and run your monthly reporting cycle — EBITDA bridge, actual vs. budget variance, working capital summary, add-back documentation, and board-ready narrative. Your sponsor gets what they need. Your management team gets clarity.',
        features: [
          'Monthly investor reporting package',
          'EBITDA bridge and adjusted EBITDA tracking',
          'Actual vs. budget and actual vs. reforecast analysis',
          'Board deck and narrative commentary',
          'Lender covenant monitoring',
          'KPI dashboard design and maintenance',
        ],
      },
      
      pmi: {
        title: 'Post-Merger Integration — Finance Workstream',
        short: 'Build financial infrastructure in the critical first 60-100 days post-close.',
        full: 'When a portfolio company closes a bolt-on acquisition, the finance function faces a hard deadline: consolidate two companies\' reporting, harmonize charts of accounts, build a combined financial model, and deliver Day 1 reporting to sponsors — all within 60 to 100 days. We run this workstream so management can keep running the business.',
        features: [
          'Day 1 reporting readiness plan',
          'Chart of accounts harmonization',
          'Combined 3-statement financial model',
          'Consolidated close calendar and reporting cadence',
          'Transition Service Agreement (TSA) tracking',
          'Post-close EBITDA normalization and add-back setup',
        ],
      },
      
      performance: {
        title: 'Performance Management & Dashboards',
        short: 'Real-time visibility into what drives performance across your portfolio.',
        full: 'PE sponsors need real-time visibility into what\'s driving performance across their portfolio. We design driver-based financial models and sponsor-facing dashboards that connect operational metrics directly to EBITDA outcomes — giving your management team and investors a single, trusted view of performance.',
        features: [
          'Driver-based financial modeling',
          'Sponsor and management dashboard design',
          'Real-time KPI monitoring',
          'Portfolio-level reporting standardization',
          'Data integration and automation',
          'Executive-level data visualization',
        ],
      },
      
      valueCreation: {
        title: 'Value Creation Planning',
        short: 'Turn the investment thesis into an executable financial roadmap.',
        full: 'Value creation is why PE exists. We translate your investment thesis into a live operating plan — defining the EBITDA levers, the operational KPIs, the milestone tracking, and the accountability structure that keeps management and sponsors aligned from entry to exit.',
        features: [
          'Investment thesis to operating plan translation',
          'EBITDA lever identification and prioritization',
          'Value creation roadmap with milestones',
          'Scenario and sensitivity analysis',
          'Exit readiness financial preparation',
          'Ongoing value creation tracking and reporting',
        ],
      },
    },
    
    // Who We Serve Page
    whoWeServe: {
      title: 'Who We Serve',
      subtitle: 'We work exclusively with PE sponsors, portfolio companies, and operators navigating growth, integration, and value creation.',
      whatClientsSay: 'What Our Clients Say',
      realResults: 'Real results from real partnerships',
      ctaTitle: 'Ready to build institutional-grade finance?',
      ctaSubtitle: 'Whether you\'re post-close, scaling a platform, or preparing for exit—we\'re built for this.',
      letsTalk: 'Let\'s Talk',
      howWeHelp: 'How We Help',
      
      peBackedCompanies: {
        title: 'PE-Backed Portfolio Companies',
        subtitle: 'PE-ready reporting from Day 1',
        description: 'You were just acquired. Your PE sponsor expects monthly EBITDA reporting, board packs, and working capital dashboards — starting next month. We build the infrastructure and run the reporting so your team doesn\'t drown in it.',
        painPoints: [
          'Sponsor pressure from Day 1',
          'Understaffed finance team',
          'First PE ownership cycle',
        ],
        benefits: [
          'Monthly EBITDA reporting package',
          'Board-ready financial presentations',
          'Adjusted EBITDA framework and add-back tracking',
          'Working capital and cash flow dashboards',
          'Sponsor communication support',
        ],
      },
      
      boltOnAcquisitions: {
        title: 'Portfolio Companies Doing Bolt-On Acquisitions',
        subtitle: 'Finance integration that keeps pace with growth',
        description: 'Your PE sponsor\'s value creation strategy depends on bolt-ons. But every acquisition doubles the complexity of your reporting. We run the finance workstream of every integration so management stays focused on operating the business.',
        painPoints: [
          'Two sets of books to consolidate',
          'Tight Day 1 reporting deadline',
          'Chart of accounts chaos',
          'Sponsor expects clean consolidated financials fast',
        ],
        benefits: [
          'Chart of accounts harmonization',
          'Combined 3-statement financial model',
          'Consolidated close calendar',
          'Day 1 reporting readiness',
          'Post-close EBITDA normalization',
        ],
      },
      
      highGrowthSMBs: {
        title: 'High-Growth SMBs',
        subtitle: 'Enterprise-grade finance without the overhead',
        description: 'You\'re scaling fast and your finance processes haven\'t caught up. We bring the FP&A discipline, driver-based models, and executive dashboards that growing companies need — without the overhead of building it in-house.',
        painPoints: [
          'Reactive financial reporting',
          'No forecasting infrastructure',
          'Board or investor pressure',
          'Spreadsheet chaos',
        ],
        benefits: [
          'Scalable FP&A processes',
          'Driver-based financial models',
          'Executive dashboards and KPI tracking',
          'Board-ready reporting packages',
          'Fundraising support',
        ],
      },
    },
    
    // About Page
    about: {
      title: 'About Nezz Consulting',
      subtitle: 'We exist to make enterprise-level financial rigor accessible to growing businesses and PE-backed companies.',
      ourMission: 'Our Mission',
      missionText: 'Financial complexity shouldn\'t stand in the way of good decisions. We bring clarity, structure, and actionable insights to organizations that need sophisticated financial planning without the overhead of building it in-house. Whether you\'re scaling fast or optimizing for value creation, we deliver the tools and expertise that turn data into direction.',
      additionalContext: 'Nezz Consulting works exclusively with PE-backed companies and high-growth SMBs that need enterprise-grade financial infrastructure without the enterprise overhead. Our engagements are structured, time-bound, and built to leave your finance team stronger and more capable when we\'re done — not dependent on us indefinitely.',
      ourValues: 'Our Values',
      valuesSubtitle: 'The principles that guide how we work',
      meetFounder: 'Meet the Founder',
      ourPartners: 'Our Partners',
      partnersSubtitle: 'Strategic partnerships that enhance our service delivery',
      swiftFinancePartner: 'Official partner of Swift Finance (by Exia Conseil) — the leading FP&A platform for PE-backed companies. We implement and configure Swift Finance as part of our portfolio company reporting engagements.',
      letsWork: 'Let\'s work together',
      letsWorkText: 'Ready to bring clarity and structure to your financial operations? Let\'s talk.',
      getInTouch: 'Get in Touch',
      
      values: [
        {
          title: 'Clarity Over Complexity',
          description: 'Financial rigor doesn\'t have to be complicated. We cut through the noise to deliver insights that are clear, actionable, and actually useful.',
        },
        {
          title: 'Built to Scale',
          description: 'We don\'t build temporary fixes. Every solution is designed to grow with your business and support you through the next stage of growth.',
        },
        {
          title: 'Partnership, Not Projects',
          description: 'We\'re not here to drop a deliverable and disappear. We invest in understanding your business and building solutions that genuinely move the needle.',
        },
      ],
    },
    
    // Insights Page
    insights: {
      title: 'Insights',
      subtitle: 'Practical perspectives on FP&A, business intelligence, and strategic finance.',
      readMore: 'Read More',
      newsletterTitle: 'Subscribe to Our Newsletter',
      newsletterSubtitle: 'Get practical insights on FP&A, BI, and strategic finance delivered to your inbox.',
      emailPlaceholder: 'Enter your email',
      subscribe: 'Subscribe',
      subscribing: 'Subscribing...',
      ctaTitle: 'Want to discuss your specific challenge?',
      ctaSubtitle: 'Let\'s explore how we can help you build better financial processes.',
      articles: [
        {
          title: 'The 90-day finance build: what happens to your reporting function after PE acquisition',
          status: 'Coming Soon',
        },
        {
          title: 'Adjusted EBITDA for portfolio companies: what your PE sponsor actually wants to see',
          status: 'Coming Soon',
        },
        {
          title: 'Post-merger integration — the finance workstream no one talks about',
          status: 'Coming Soon',
        },
      ],
    },
    
    // Contact Page
    contact: {
      title: 'Let\'s Talk',
      subtitle: 'Ready to bring clarity to your financial operations? Get in touch and let\'s discuss how we can help.',
      getInTouch: 'Get in Touch',
      location: 'Location',
      email: 'Email',
      phone: 'Phone',
      sendMessage: 'Send Us a Message',
      name: 'Name',
      company: 'Company',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      whyNezz: 'Why Nezz Consulting?',
      reasons: [
        '$1B+ in budget and forecast managed',
        'Swift Finance certified partner',
        'Tailored solutions for SMBs & PE-backed companies',
        'Proven expertise in FP&A and BI',
      ],
    },
    
    // Footer
    footer: {
      tagline: 'Finance infrastructure for PE-backed companies — from post-close to exit-ready.',
      quickLinks: 'Quick Links',
      services: 'Services',
      contact: 'Contact',
      rights: 'All rights reserved.',
      servicesList: {
        fpa: 'FP&A & Investor Reporting',
        pmi: 'Post-Merger Integration',
        performance: 'Performance Management',
        valueCreation: 'Value Creation Planning',
      },
    },
  },
  
  fr: {
    // Page Metadata
    meta: {
      home: {
        title: 'Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      services: {
        title: 'Services | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      pricing: {
        title: 'Tarifs | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      whoWeServe: {
        title: 'Qui nous servons | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      about: {
        title: 'À propos | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      insights: {
        title: 'Perspectives | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      contact: {
        title: 'Contact | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
      pmi: {
        title: 'Intégration post-fusion | Nezz Consulting',
        description: 'Infrastructure financière pour les entreprises soutenues par PE — FP&A, reporting investisseur et intégration post-fusion. Basé à Montréal, disponible partout au Canada.',
      },
    },
    
    // Navbar
    nav: {
      home: 'Accueil',
      services: 'Services',
      pricing: 'Tarification',
      pmi: 'Intégration Post-Fusion',
      whoWeServe: 'Nos Clients',
      about: 'À Propos',
      insights: 'Perspectives',
      contact: 'Contact',
    },
    
    // Home Page
    home: {
      heroTitle: 'Infrastructure financière pour les entreprises soutenues par capital-investissement',
      heroHighlight: 'de la post-acquisition à la préparation à la sortie',
      heroSubtitle: 'Nous construisons l\'infrastructure de reporting, le FP&A de niveau investisseur et la fonction financière post-fusion que les sponsors de capital-investissement exigent — déployés rapidement, par des opérateurs qui l\'ont déjà fait.',
      getStarted: 'Planifier une consultation',
      exploreServices: 'Voir nos services',
      trustBar: 'Approuvé par les sociétés soutenues par PE et les sociétés de portefeuille en Amérique du Nord',
      smbs: 'Entreprises en Croissance',
      peCompanies: 'Sociétés de Portefeuille PE',
      strategicPartners: 'Partenaires Stratégiques',
      ourServices: 'Nos Services',
      servicesSubtitle: 'Planification financière, rapports et intégration de qualité PE - livrés par des opérateurs qui comprennent le playbook.',
      whoWeServeTitle: 'Nos Clients',
      whoWeServeSubtitle: 'Conçu pour les sponsors PE, les sociétés de portefeuille et les opérateurs naviguant une croissance complexe.',
      learnMore: 'En Savoir Plus',
      ctaTitle: 'Prêt à améliorer votre fonction financière?',
      ctaSubtitle: 'Discutons de la façon dont nous pouvons vous aider à construire une infrastructure financière de qualité institutionnelle.',
      scheduleConsultation: 'Planifier une Consultation',
      problem90DayTitle: 'Le problème des 90 jours post-acquisition',
      problem90DayText: 'Lorsqu\'une entreprise est acquise par un fonds de capital-investissement, la direction financière doit se transformer du jour au lendemain. Les investisseurs attendent des rapports EBITDA mensuels, la documentation des retraitements, des board packs et des tableaux de bord du fonds de roulement — souvent avant que l\'équipe de direction ne les ait jamais produits. Nous résolvons ce problème en 90 jours. Nous intervenons, évaluons l\'infrastructure financière existante et construisons tout ce que votre sponsor PE a besoin de voir — dans les délais, chaque mois.',
      problem90DayCTA: 'Voir comment ça fonctionne',
      peBackedCard: {
        title: 'Entreprises de portefeuille soutenues par PE',
        body: 'L\'actionnariat PE change tout dans le fonctionnement de votre direction financière. Nous construisons l\'infrastructure de reporting, le cadre EBITDA et les packages prêts pour le conseil d\'administration que votre sponsor attend — dès le premier jour post-acquisition.',
      },
      smbCard: {
        title: 'PME à forte croissance',
        body: 'Vous évoluez rapidement et vos processus financiers n\'ont pas suivi. Nous apportons la discipline FP&A de niveau entreprise, des modèles basés sur les inducteurs et des tableaux de bord dirigeants — construits pour le rythme d\'une entreprise en croissance.',
      },
    },
    
    // Stats
    stats: {
      budgetManaged: '$1B+ Budgets et prévisions gérés',
      yearsExperience: '15+ Années d\'expérience PE & FP&A',
      projectsDelivered: '50+ Projets livrés',
      clientSatisfaction: '100% Satisfaction client',
    },
    
    // Post-Merger Integration Page
    pmi: {
      heroTitle: 'Intégration post-fusion — volet financier',
      heroSubtitle: 'Nous construisons l\'infrastructure financière qui fait fonctionner votre acquisition complémentaire.',
      getStarted: 'Parlez-nous de votre transaction',
      problemTitle: 'Le problème',
      problemDescription: 'La plupart des entreprises de portefeuille qui finalisent une acquisition complémentaire ne sont pas prêtes pour ce qui suit. Deux entreprises. Deux plans comptables. Deux calendriers de clôture. Deux formats de reporting. Un sponsor PE qui attend des états financiers consolidés dans les 30 jours. L\'équipe financière est dépassée avant même que l\'encre soit sèche. Nous avons déjà vécu cette situation. Nous savons exactement ce qui doit se passer et dans quel ordre.',
      solutionTitle: 'Ce que nous livrons',
      phase1Title: 'Jours 1–30 : Fondation',
      phase1Items: [
        'Évaluation des lacunes de reporting',
        'Cartographie et harmonisation du plan comptable',
        'Alignement du calendrier de clôture',
        'Package de reporting Jour 1',
      ],
      phase2Title: 'Jours 31–60 : Construction',
      phase2Items: [
        'Modèle financier à 3 états combiné',
        'Cadre EBITDA ajusté et registre des retraitements',
        'Modèles de reporting direction et sponsors',
        'Base de référence du fonds de roulement',
      ],
      phase3Title: 'Jours 61–100 : Stabilisation',
      phase3Items: [
        'Première clôture mensuelle consolidée complète',
        'Livraison du board pack',
        'Tableau de bord KPI opérationnel',
        'Passation de relais et documentation pour l\'équipe financière',
      ],
      ctaTitle: 'Prêt à intégrer rapidement?',
      ctaSubtitle: 'Construisons l\'infrastructure financière dont votre acquisition a besoin.',
      scheduleCall: 'Parlez-nous de votre transaction',
    },

    
    // Services Page
    services: {
      title: 'Services',
      subtitle: 'FP&A, rapports et infrastructure financière de qualité PE - conçus pour la vitesse et les normes institutionnelles.',
      notSure: 'Vous ne savez pas quel service vous avez besoin?',
      notSureText: 'Parlons de où vous en êtes et où vous devez être.',
      
      fpa: {
        title: 'FP&A de niveau PE et rapports investisseurs',
        short: 'Planification et rapports financiers de qualité institutionnelle.',
        full: 'L\'actionnariat PE exige un niveau de reporting financier entièrement différent. Nous concevons et gérons votre cycle de reporting mensuel — pont EBITDA, écart réel vs budget, résumé du fonds de roulement, documentation des retraitements et narration prête pour le conseil. Votre sponsor obtient ce dont il a besoin. Votre équipe de direction obtient de la clarté.',
        features: [
          'Package de reporting investisseur mensuel',
          'Pont EBITDA et suivi de l\'EBITDA ajusté',
          'Analyse réel vs budget et réel vs reprévision',
          'Présentation conseil et commentaire narratif',
          'Suivi des covenants de prêteurs',
          'Conception et maintenance du tableau de bord KPI',
        ],
      },
      
      pmi: {
        title: 'Intégration post-fusion — volet financier',
        short: 'Construction d\'infrastructure financière dans les 60-100 premiers jours critiques.',
        full: 'Lorsqu\'une entreprise de portefeuille finalise une acquisition complémentaire, la direction financière fait face à une échéance ferme : consolider le reporting de deux entreprises, harmoniser les plans comptables, construire un modèle financier combiné et livrer le reporting Jour 1 aux sponsors — le tout en 60 à 100 jours. Nous gérons ce volet pour que la direction puisse continuer à opérer l\'entreprise.',
        features: [
          'Plan de préparation au reporting Jour 1',
          'Harmonisation du plan comptable',
          'Modèle financier à 3 états combiné',
          'Calendrier de clôture consolidé et cadence de reporting',
          'Suivi de l\'accord de services transitoires (TSA)',
          'Normalisation post-acquisition de l\'EBITDA et mise en place des retraitements',
        ],
      },
      
      performance: {
        title: 'Gestion de la performance et tableaux de bord',
        short: 'Visibilité en temps réel sur ce qui génère la performance.',
        full: 'Les sponsors PE ont besoin d\'une visibilité en temps réel sur ce qui génère la performance au sein de leur portefeuille. Nous concevons des modèles financiers basés sur les inducteurs et des tableaux de bord destinés aux sponsors qui relient les métriques opérationnelles directement aux résultats EBITDA — offrant à votre équipe de direction et à vos investisseurs une vue unique et fiable de la performance.',
        features: [
          'Modélisation financière basée sur les inducteurs',
          'Conception de tableaux de bord pour sponsors et direction',
          'Surveillance des KPI en temps réel',
          'Standardisation du reporting au niveau du portefeuille',
          'Intégration et automatisation des données',
          'Visualisation des données au niveau exécutif',
        ],
      },
      
      valueCreation: {
        title: 'Planification de la création de valeur',
        short: 'Transformer la thèse d\'investissement en feuille de route exécutable.',
        full: 'La création de valeur est la raison d\'être du capital-investissement. Nous traduisons votre thèse d\'investissement en un plan opérationnel actif — définissant les leviers EBITDA, les KPI opérationnels, le suivi des jalons et la structure de responsabilisation qui maintient la direction et les sponsors alignés de l\'entrée à la sortie.',
        features: [
          'Traduction de la thèse d\'investissement en plan opérationnel',
          'Identification et priorisation des leviers EBITDA',
          'Feuille de route de création de valeur avec jalons',
          'Analyse de scénarios et de sensibilité',
          'Préparation financière à la sortie',
          'Suivi et reporting continus de la création de valeur',
        ],
      },
    },
    
    // Who We Serve Page
    whoWeServe: {
      title: 'Nos Clients',
      subtitle: 'Nous travaillons exclusivement avec des sponsors PE, des sociétés de portefeuille et des opérateurs naviguant la croissance, l\'intégration et la création de valeur.',
      whatClientsSay: 'Ce que Disent nos Clients',
      realResults: 'Des résultats réels de partenariats réels',
      ctaTitle: 'Prêt à construire une finance de niveau institutionnel?',
      ctaSubtitle: 'Que vous soyez post-acquisition, en train de développer une plateforme ou en préparation de sortie—nous sommes faits pour cela.',
      letsTalk: 'Discutons',
      howWeHelp: 'Comment Nous Aidons',
      
      peBackedCompanies: {
        title: 'Entreprises de portefeuille soutenues par PE',
        subtitle: 'Reporting prêt pour PE dès le Jour 1',
        description: 'Vous venez d\'être acquis. Votre sponsor PE attend des rapports EBITDA mensuels, des board packs et des tableaux de bord du fonds de roulement — dès le mois prochain. Nous construisons l\'infrastructure et gérons le reporting pour que votre équipe ne soit pas submergée.',
        painPoints: [
          'Pression des sponsors dès le Jour 1',
          'Équipe financière sous-dimensionnée',
          'Premier cycle d\'actionnariat PE',
        ],
        benefits: [
          'Package de reporting EBITDA mensuel',
          'Présentations financières prêtes pour le conseil',
          'Cadre EBITDA ajusté et suivi des retraitements',
          'Tableaux de bord du fonds de roulement et flux de trésorerie',
          'Support de communication avec les sponsors',
        ],
      },
      
      boltOnAcquisitions: {
        title: 'Entreprises de portefeuille réalisant des acquisitions complémentaires',
        subtitle: 'Intégration financière qui suit le rythme de la croissance',
        description: 'La stratégie de création de valeur de votre sponsor PE repose sur les acquisitions complémentaires. Mais chaque acquisition double la complexité de votre reporting. Nous gérons le volet financier de chaque intégration pour que la direction reste concentrée sur l\'exploitation de l\'entreprise.',
        painPoints: [
          'Deux jeux de livres à consolider',
          'Délai serré pour le reporting Jour 1',
          'Chaos dans le plan comptable',
          'Le sponsor attend des états financiers consolidés propres rapidement',
        ],
        benefits: [
          'Harmonisation du plan comptable',
          'Modèle financier à 3 états combiné',
          'Calendrier de clôture consolidé',
          'Préparation au reporting Jour 1',
          'Normalisation de l\'EBITDA post-acquisition',
        ],
      },
      
      highGrowthSMBs: {
        title: 'PME à forte croissance',
        subtitle: 'Finance de niveau entreprise sans les coûts associés',
        description: 'Vous évoluez rapidement et vos processus financiers n\'ont pas suivi. Nous apportons la discipline FP&A, les modèles basés sur les inducteurs et les tableaux de bord dirigeants dont les entreprises en croissance ont besoin — sans les coûts d\'une équipe interne.',
        painPoints: [
          'Reporting financier réactif',
          'Absence d\'infrastructure de prévision',
          'Pression du conseil ou des investisseurs',
          'Chaos des feuilles de calcul',
        ],
        benefits: [
          'Processus FP&A évolutifs',
          'Modèles financiers basés sur les inducteurs',
          'Tableaux de bord dirigeants et suivi des KPI',
          'Packages de reporting prêts pour le conseil',
          'Support pour la levée de fonds',
        ],
      },
    },
    
    // About Page
    about: {
      title: 'À Propos de Nezz Consulting',
      subtitle: 'Nous existons pour rendre la rigueur financière de niveau entreprise accessible aux entreprises en croissance et aux sociétés soutenues par PE.',
      ourMission: 'Notre Mission',
      missionText: 'La complexité financière ne devrait pas faire obstacle aux bonnes décisions. Nous apportons clarté, structure et informations exploitables aux organisations qui ont besoin d\'une planification financière sophistiquée sans les frais généraux de la construire en interne. Que vous évoluer rapidement ou optimisiez pour la création de valeur, nous fournissons les outils et l\'expertise qui transforment les données en direction.',
      ourValues: 'Nos Valeurs',
      valuesSubtitle: 'Les principes qui guident notre travail',
      meetFounder: 'Rencontrez le Fondateur',
      ourPartners: 'Nos Partenaires',
      partnersSubtitle: 'Partenariats stratégiques qui améliorent notre prestation de services',
      letsWork: 'Travaillons ensemble',
      letsWorkText: 'Prêt à apporter clarté et structure à vos opérations financières? Discutons.',
      getInTouch: 'Contactez-nous',
      
      values: [
        {
          title: 'Clarté plutôt que Complexité',
          description: 'La rigueur financière n\'a pas besoin d\'être compliquée. Nous coupons à travers le bruit pour fournir des informations claires, exploitables et réellement utiles.',
        },
        {
          title: 'Conçu pour Évoluer',
          description: 'Nous ne construisons pas de solutions temporaires. Chaque solution est conçue pour croître avec votre entreprise et vous soutenir à travers la prochaine étape de croissance.',
        },
        {
          title: 'Partenariat, Pas Projets',
          description: 'Nous ne sommes pas là pour déposer un livrable et disparaître. Nous investissons dans la compréhension de votre entreprise et la construction de solutions qui font vraiment bouger les choses.',
        },
      ],
    },
    
    // Insights Page
    insights: {
      title: 'Perspectives',
      subtitle: 'Perspectives pratiques sur FP&A, l\'intelligence d\'affaires et la finance stratégique.',
      readMore: 'Lire Plus',
      newsletterTitle: 'Abonnez-vous à Notre Infolettre',
      newsletterSubtitle: 'Recevez des informations pratiques sur FP&A, BI et la finance stratégique dans votre boîte de réception.',
      emailPlaceholder: 'Entrez votre email',
      subscribe: 'S\'abonner',
      subscribing: 'Inscription...',
      ctaTitle: 'Vous voulez discuter de votre défi spécifique?',
      ctaSubtitle: 'Explorons comment nous pouvons vous aider à construire de meilleurs processus financiers.',
    },
    
    // Contact Page
    contact: {
      title: 'Discutons',
      subtitle: 'Prêt à apporter de la clarté à vos opérations financières? Contactez-nous et discutons de la façon dont nous pouvons vous aider.',
      getInTouch: 'Contactez-nous',
      location: 'Emplacement',
      email: 'Email',
      phone: 'Téléphone',
      sendMessage: 'Envoyez-nous un Message',
      name: 'Nom',
      company: 'Entreprise',
      message: 'Message',
      send: 'Envoyer le Message',
      sending: 'Envoi...',
      whyNezz: 'Pourquoi Nezz Consulting?',
      reasons: [
        'Plus de 1 milliard $ en budgets et prévisions gérés',
        'Partenaire certifié Swift Finance',
        'Solutions sur mesure pour PME et sociétés soutenues par PE',
        'Expertise éprouvée en FP&A et BI',
      ],
    },
    
    // Footer
    footer: {
      tagline: 'Infrastructure financière pour les entreprises soutenues par capital-investissement — de la post-acquisition à la préparation à la sortie.',
      quickLinks: 'Liens Rapides',
      services: 'Services',
      contact: 'Contact',
      rights: 'Tous droits réservés.',
      servicesList: {
        fpa: 'FP&A et rapports investisseurs',
        pmi: 'Intégration post-fusion',
        performance: 'Gestion de la performance',
        valueCreation: 'Planification de création de valeur',
      },
    },
  },
};

export const useTranslation = (language) => {
  return translations[language] || translations.en;
};
